import {ApiClients} from '@salesforce/commerce-sdk-react/hooks/types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import axios from 'axios'
import Cookies from 'js-cookie'
import {SEGMENT_PRICE_TEST_COOKIE} from '../utils/constants'

export const getAbPriceTestSegmentCookie = () => {
    // SSR guard – js-cookie only works in the browser
    if (typeof window === 'undefined') return undefined
    return Cookies.get(SEGMENT_PRICE_TEST_COOKIE)
}

export const buildPriceTestParam = () => {
    const variant = getAbPriceTestSegmentCookie()
    return variant ? {c_hec_priceTestVariant: variant} : {}
}

export const buildCategoryLink = ({
    appOrigin,
    categoryID
}: {
    appOrigin: string
    categoryID: string
}) => `${appOrigin}/search?cgid=${categoryID}`

// Utility function to get the authorization token
const getAuthToken = async (
    siteId: string,
    getTokenWhenReady: () => Promise<string>
): Promise<string | null> => {
    return localStorage.getItem(`access_token_${siteId}`) ?? (await getTokenWhenReady())
}

export const getProductById = async (
    productId: string,
    api: ApiClients,
    getTokenWhenReady: () => Promise<string>
) => {
    const config = getConfig()
    const {
        app: {commerceAPI}
    } = config
    const siteId: string = commerceAPI.parameters.siteId
    let token: string
    try {
        const authToken = await getAuthToken(siteId, getTokenWhenReady)
        if (!authToken) {
            throw new Error('Failed to retrieve auth token.')
        }
        token = authToken
    } catch (authError) {
        console.error('Error fetching auth token:', authError)
        throw new Error('Failed to authenticate request.')
    }
    try {
        const product = await api.shopperProducts.getProduct({
            parameters: {
                id: productId,
                allImages: true,
                ...buildPriceTestParam()
            },
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return product
    } catch (error) {
        console.error('Error getting product details by id:', error)
        throw new Error('Failed to fetch product details.')
    }
}

export const getProductsByIds = async (
    productIds: string[],
    api: ApiClients,
    getTokenWhenReady: () => Promise<string>
): Promise<any> => {
    if (!productIds || productIds.length === 0) {
        console.warn('getProductsByIds called with empty productIds array.')
        return []
    }
    const config = getConfig()
    const {
        app: {commerceAPI}
    } = config

    const siteId: string = commerceAPI.parameters.siteId
    let token: string
    try {
        const authToken = await getAuthToken(siteId, getTokenWhenReady)
        if (!authToken) {
            throw new Error('Failed to retrieve auth token.')
        }
        token = authToken
    } catch (authError) {
        console.error('Error fetching auth token:', authError)
        throw new Error('Failed to authenticate request.')
    }
    try {
        const products = await api.shopperProducts.getProducts({
            parameters: {
                ids: productIds.join(','),
                allImages: true,
                ...buildPriceTestParam()
            },
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
        return products
    } catch (error) {
        console.error('Error getting product details by ids:', error)
        throw new Error('Failed to fetch product details.')
    }
}
export const getCustomProductPrice = async (
    productIds: string[],
    appOrigin: string,
    getTokenWhenReady: any
) => {
    const config = getConfig()
    const orgId: string = config.app.commerceAPI.parameters.organizationId
    const siteId: string = config.app.commerceAPI.parameters.siteId
    let token: string | null
    try {
        token = await getAuthToken(siteId, getTokenWhenReady)
    } catch (authError) {
        console.error('Error fetching auth token:', authError)
        throw new Error('Failed to authenticate request.')
    }
    if (!token) {
        console.error('Auth token is null or undefined')
        throw new Error('Auth token is null or undefined')
    }
    try {
        const productData = await axios.get(
            `${appOrigin}/mobify/proxy/api/custom/product/v1/organizations/${orgId}/products`,
            {
                params: {
                    c_productIds: productIds.join(','),
                    siteId,
                    ...buildPriceTestParam()
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )
        return productData
    } catch (e) {
        console.error('Error getting product pricing data by ids:', e)
        throw new Error('Failed to fetch product pricing details.')
    }
}

// In-memory cache for the account manager token.
// Uses a single in-flight promise to prevent thundering-herd: when N concurrent
// requests all need a token, only 1 HTTP call is made to account.demandware.com.
let tokenPromise: Promise<{access_token: string; expires_in: number}> | null = null
let cachedAccountMangerToken: string | null = null
let tokenExpiresAt = 0

// 30-second buffer so we refresh before the token actually expires
const EXPIRY_BUFFER_MS = 30_000

export const getValidAccountMangerToken = async (): Promise<string | null> => {
    const now = Date.now()

    // Return cached token if still valid (with buffer before expiry)
    if (cachedAccountMangerToken && now < tokenExpiresAt - EXPIRY_BUFFER_MS) {
        return cachedAccountMangerToken
    }

    // Deduplicate: if a fetch is already in-flight, reuse the same promise
    if (!tokenPromise) {
        tokenPromise = getAccountMangerOcapiToken().finally(() => {
            tokenPromise = null
        })
    }

    try {
        const {access_token, expires_in} = await tokenPromise
        cachedAccountMangerToken = access_token
        tokenExpiresAt = Date.now() + expires_in * 1000
        return cachedAccountMangerToken
    } catch (err) {
        // Clear stale state so the next request retries cleanly
        cachedAccountMangerToken = null
        tokenExpiresAt = 0
        throw err
    }
}

const getAccountManagerAuthorization = (): {Authorization: string} => {
    const clientId = process.env.ADMIN_COMMERCE_API_CLIENT_ID || ''
    const clientSecret = process.env.ADMIN_PWA_KIT_SLAS_CLIENT_SECRET || ''
    return {
        Authorization: `Basic ${btoa(`${clientId}:${clientSecret}`)}`
    }
}

export const getAccountMangerOcapiToken = async (): Promise<{
    access_token: string
    expires_in: number
}> => {
    try {
        const config = getConfig()
        const {
            app: {commerceAPI}
        } = config

        // Extract realm key and instance identifier from organizationId
        // organizationId format: f_ecom_{REALM_KEY}_{INSTANCE_IDENTIFIER}
        const orgId = commerceAPI.parameters.organizationId
        const orgIdParts = orgId.split('_')
        const realmKey = orgIdParts[2] as string // bfkw
        const instanceIdentifier = orgIdParts[3] as string // 002
        const scopeIdentifier = `${realmKey}_${instanceIdentifier}`

        const response = await fetch('https://account.demandware.com/dwsso/oauth2/access_token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                ...getAccountManagerAuthorization()
            },
            body: new URLSearchParams({
                grant_type: 'client_credentials',
                scope: `SALESFORCE_COMMERCE_API:${scopeIdentifier} sfcc.preferences`
            })
        })

        if (!response.ok) {
            throw new Error('Failed to fetch token')
        }

        return response.json()
    } catch (error) {
        console.error('Error fetching account manager OCAPI token:', error)
        throw new Error('Failed to fetch account manager OCAPI token')
    }
}
